import { resolveDashboardRoute } from '@/utils/authCrossTabSync';
import { ROUTES } from '@/utils/pathRoutes';
import { useAuthStore } from '@/store/useAuthStore';

export type DbRestorePhase = 'restoring' | 'completed' | 'failed';

export type DbRestoreSignalState = {
  phase: Exclude<DbRestorePhase, 'failed'>;
  excludeUserId?: string;
  t?: number;
};

export type DbRestoreProgress = {
  percent: number;
  current: number;
  total: number;
  serviceName?: string;
  tableName?: string;
};

const DB_RESTORE_LOG_PREFIX = '[DbRestoreSignalR]';

/** Log trace theo bước — filter console: DbRestoreSignalR */
export function logDbRestoreTrace(step: string, detail?: unknown): void {
  if (detail !== undefined) {
    console.info(`${DB_RESTORE_LOG_PREFIX}[${step}]`, detail);
  } else {
    console.info(`${DB_RESTORE_LOG_PREFIX}[${step}]`);
  }
}

/** Site/tenant hiện tại — dùng làm prefix localStorage (không hardcode TOPPION). */
export function getDbRestoreSiteKey(): string {
  const tenantID = useAuthStore.getState().tenantID;
  const site = String(tenantID ?? '').trim();
  return site || 'unknown';
}

function dbRestoreSignalKey(site = getDbRestoreSiteKey()): string {
  return `${site}:db-restore-signal`;
}

function dbRestoreProgressKey(site = getDbRestoreSiteKey()): string {
  return `${site}:db-restore-progress`;
}

function dbRestoreChannelName(site = getDbRestoreSiteKey()): string {
  return `${site}:db-restore-channel`;
}

function dbRestoreOperatorChannelName(site = getDbRestoreSiteKey()): string {
  return `${site}:db-restore-operator-channel`;
}

function dbRestoreProgressChannelName(site = getDbRestoreSiteKey()): string {
  return `${site}:db-restore-progress-channel`;
}

const LEGACY_DB_RESTORE_SIGNAL_KEY = 'TOPPION:db-restore-signal';
const LEGACY_DB_RESTORE_PROGRESS_KEY = 'TOPPION:db-restore-progress';

// Sự kiện cùng-tab
const DB_RESTORE_LOCAL_PHASE_EVENT = 'TOPPION:db-restore-local-phase';
const DB_RESTORE_LOCAL_PROGRESS_EVENT = 'TOPPION:db-restore-local-progress';

function emitLocalDbRestorePhase(phase: DbRestorePhase): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent<DbRestorePhase>(DB_RESTORE_LOCAL_PHASE_EVENT, { detail: phase }));
  } catch {
    // ignore
  }
}

function emitLocalDbRestoreProgress(progress: DbRestoreProgress): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent<DbRestoreProgress>(DB_RESTORE_LOCAL_PROGRESS_EVENT, { detail: progress }));
  } catch {
    // ignore
  }
}

export function isDatabaseRoute(pathname?: string): boolean {
  const path = (pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '')).toLowerCase();
  return path.includes('/database');
}

export function shouldIgnoreDbRestoreSignal(excludeUserId?: string | null): boolean {
  if (!excludeUserId) return false;
  const profileId = useAuthStore.getState().profile?.id;
  return Boolean(profileId && String(profileId) === String(excludeUserId));
}

export function isDbRestoreLockRoute(pathname?: string): boolean {
  const path = (pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '')).toLowerCase();
  return path.includes(ROUTES.DB_RESTORE_LOCK.toLowerCase());
}

export function buildDbRestoreLockHref(
  phase: Exclude<DbRestorePhase, 'failed'>,
  returnUrl?: string | null
): string {
  const params = new URLSearchParams({ state: phase });
  if (returnUrl) params.set('returnUrl', returnUrl);
  return `${ROUTES.DB_RESTORE_LOCK}?${params.toString()}`;
}

export function getCurrentPageReturnUrl(): string {
  if (typeof window === 'undefined') return ROUTES.HOME;
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function parseDbRestoreSignal(notification: Record<string, unknown>): {
  phase: string;
  excludeUserId?: string;
} {
  const data = (notification.data ?? notification.Data ?? {}) as Record<string, unknown>;
  const phase = String(
    data.phase ?? data.Phase ?? notification.body ?? notification.Body ?? ''
  ).toLowerCase();
  const excludeUserId = (data.excludeUserId ?? data.ExcludeUserId) as string | undefined;
  return { phase, excludeUserId };
}

export function parseDbRestoreProgress(notification: Record<string, unknown>): DbRestoreProgress | null {
  const data = (notification.data ?? notification.Data ?? {}) as Record<string, unknown>;
  const phase = String(data.phase ?? data.Phase ?? notification.body ?? notification.Body ?? '').toLowerCase();
  if (phase !== 'progress') return null;

  const current = Number(data.current ?? data.Current ?? 0);
  const total = Number(data.total ?? data.Total ?? 0);
  const percentRaw = Number(data.percent ?? data.Percent ?? 0);
  const percent = total > 0
    ? Math.min(100, Math.max(0, percentRaw || Math.round((current / total) * 100)))
    : Math.min(100, Math.max(0, percentRaw));

  return {
    percent,
    current,
    total,
    serviceName: String(data.serviceName ?? data.ServiceName ?? ''),
    tableName: String(data.tableName ?? data.TableName ?? ''),
  };
}

export function readDbRestoreSignalState(): DbRestoreSignalState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(dbRestoreSignalKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DbRestoreSignalState;
    if (parsed.phase === 'restoring' || parsed.phase === 'completed') {
      return parsed;
    }
  } catch {
    // ignore
  }

  return null;
}

function getReturnUrlFromCurrentLocation(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('returnUrl');
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}

/** Hủy khóa restore — xóa state, cho phép user quay về trang chủ/dashboard. */
export function abortDbRestoreLock(returnUrl?: string | null): void {
  if (typeof window === 'undefined') return;

  logDbRestoreTrace('12-abort-db-restore-lock', { returnUrl, site: getDbRestoreSiteKey() });
  clearDbRestoreSignalState();
  clearDbRestoreProgress();

  try {
    const channel = new BroadcastChannel(dbRestoreChannelName());
    channel.postMessage({ phase: 'failed' as DbRestorePhase });
    channel.close();
  } catch {
    // ignore
  }

  emitLocalDbRestorePhase('failed');

  if (isDbRestoreLockRoute()) {
    navigateToDashboardAfterDbRestore(returnUrl ?? getReturnUrlFromCurrentLocation());
  }
}

export function clearDbRestoreSignalState(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(dbRestoreSignalKey());
  } catch {
    // ignore
  }
}

export function shouldBypassDbRestoreLock(pathname?: string): boolean {
  if (isDbRestoreLockRoute(pathname)) return true;

  const state = readDbRestoreSignalState();
  if (state?.phase === 'restoring' && isDatabaseRoute(pathname) && shouldIgnoreDbRestoreSignal(state.excludeUserId)) {
    return true;
  }

  return false;
}

export function enforceDbRestoreLockIfNeeded(pathname?: string): boolean {
  if (typeof window === 'undefined') return false;
  if (shouldBypassDbRestoreLock(pathname)) return false;

  const state = readDbRestoreSignalState();
  if (state?.phase !== 'restoring') return false;

  if (!isDbRestoreLockRoute(pathname)) {
    logDbRestoreTrace('12-enforce-db-restore-lock', { pathname, site: getDbRestoreSiteKey() });
    window.location.href = buildDbRestoreLockHref('restoring', getCurrentPageReturnUrl());
    return true;
  }

  return false;
}

export function clearDbRestoreProgress(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(dbRestoreProgressKey());
  } catch {
    // ignore
  }
}

/**
 * Clear toàn bộ key restore DB (site hiện tại + legacy TOPPION + mọi site còn sót).
 * Gọi khi logout — trước khi clearAuth để còn đọc được tenantID.
 */
export function clearDbRestoreStorageOnLogout(): void {
  if (typeof window === 'undefined') return;

  try {
    clearDbRestoreSignalState();
    clearDbRestoreProgress();

    // Legacy keys cũ (hardcode TOPPION)
    localStorage.removeItem(LEGACY_DB_RESTORE_SIGNAL_KEY);
    localStorage.removeItem(LEGACY_DB_RESTORE_PROGRESS_KEY);

    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.endsWith(':db-restore-signal') || key.endsWith(':db-restore-progress'))
      ) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
    logDbRestoreTrace('12-clear-db-restore-on-logout', { removed: toRemove.length + 2, site: getDbRestoreSiteKey() });
  } catch {
    // ignore
  }
}

export function dispatchDbRestoreProgress(progress: DbRestoreProgress): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(dbRestoreProgressKey(), JSON.stringify({ ...progress, t: Date.now() }));
  } catch {
    // ignore
  }

  try {
    const channel = new BroadcastChannel(dbRestoreProgressChannelName());
    channel.postMessage(progress);
    channel.close();
  } catch {
    // ignore
  }

  emitLocalDbRestoreProgress(progress);
}

export function subscribeDbRestoreProgress(
  onProgress: (progress: DbRestoreProgress) => void
): () => void {
  if (typeof window === 'undefined') return () => {};

  const progressKey = dbRestoreProgressKey();
  const progressChannel = dbRestoreProgressChannelName();

  const handleProgress = (progress: DbRestoreProgress) => {
    onProgress(progress);
  };

  const onStorage = (e: StorageEvent) => {
    if (e.storageArea !== localStorage || e.key !== progressKey || !e.newValue) return;
    try {
      const parsed = JSON.parse(e.newValue) as DbRestoreProgress;
      if (typeof parsed.percent === 'number') {
        handleProgress(parsed);
      }
    } catch {
      // ignore
    }
  };

  let channel: BroadcastChannel | null = null;
  const onBroadcast = (ev: MessageEvent) => {
    const progress = ev.data as DbRestoreProgress | undefined;
    if (progress && typeof progress.percent === 'number') {
      handleProgress(progress);
    }
  };

  const onLocalProgress = (ev: Event) => {
    const progress = (ev as CustomEvent<DbRestoreProgress>).detail;
    if (progress && typeof progress.percent === 'number') {
      handleProgress(progress);
    }
  };

  window.addEventListener('storage', onStorage);
  window.addEventListener(DB_RESTORE_LOCAL_PROGRESS_EVENT, onLocalProgress);
  try {
    channel = new BroadcastChannel(progressChannel);
    channel.addEventListener('message', onBroadcast);
  } catch {
    channel = null;
  }

  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(DB_RESTORE_LOCAL_PROGRESS_EVENT, onLocalProgress);
    channel?.removeEventListener('message', onBroadcast);
    channel?.close();
  };
}

/** Operator (excludeUserId) không redirect start; user khác → /db-restore-lock/ */
export function handleDbRestoreSignal(notification: Record<string, unknown>): void {
  logDbRestoreTrace('12-FE-handleDbRestoreSignal-start', notification);
  const authState = useAuthStore.getState();
  const { phase, excludeUserId } = parseDbRestoreSignal(notification);
  const profileId = authState.profile?.id;

  if (phase === 'progress') {
    const progress = parseDbRestoreProgress(notification);
    if (progress) {
      logDbRestoreTrace('12-progress', progress);
      dispatchDbRestoreProgress(progress);
    }
    return;
  }

  if (phase === 'start') {
    logDbRestoreTrace('12-signalR-START-received', {
      onLockPage: isDbRestoreLockRoute(),
      profileId,
      excludeUserId,
      site: getDbRestoreSiteKey(),
      notification,
    });
    dispatchDbRestorePhase('restoring', excludeUserId);
    if (shouldIgnoreDbRestoreSignal(excludeUserId)) {
      logDbRestoreTrace('12-skip-operator-start', { profileId, excludeUserId });
      return;
    }
    const target = buildDbRestoreLockHref('restoring', getCurrentPageReturnUrl());
    logDbRestoreTrace('12-redirect-start', { target });
    if (!isDbRestoreLockRoute()) {
      window.location.href = target;
    }
    return;
  }

  if (phase === 'complete') {
    logDbRestoreTrace('12-signalR-COMPLETE-received', {
      onLockPage: isDbRestoreLockRoute(),
      onDatabasePage: isDatabaseRoute(),
      profileId,
      tenantID: authState.tenantID,
      notification,
    });
    dispatchDbRestorePhase('completed');
    if (isDatabaseRoute()) {
      logDbRestoreTrace('12-operator-complete-onDatabasePage');
      dispatchDbRestoreOperatorComplete();
      return;
    }
    if (isDbRestoreLockRoute()) {
      logDbRestoreTrace('12-dispatch-complete-onLockPage');
      const href = buildDbRestoreLockHref('completed', getReturnUrlFromCurrentLocation());
      window.history.replaceState(null, '', href);
      emitLocalDbRestorePhase('completed');
      return;
    }
    const target = buildDbRestoreLockHref('completed', getCurrentPageReturnUrl());
    logDbRestoreTrace('12-redirect-complete', { target });
    redirectToDbRestoreLock('completed');
    return;
  }

  if (phase === 'failed' || phase === 'error' || phase === 'cancel' || phase === 'cancelled') {
    logDbRestoreTrace('12-restore-failed', { phase, excludeUserId });
    if (isDatabaseRoute() && shouldIgnoreDbRestoreSignal(excludeUserId)) {
      dispatchDbRestoreOperatorFailed();
    }
    abortDbRestoreLock(getReturnUrlFromCurrentLocation());
    return;
  }

  logDbRestoreTrace('12-unknown-phase', { phase, notification });
}

export function dispatchDbRestoreOperatorFailed(): void {
  if (typeof window === 'undefined') return;
  logDbRestoreTrace('12-operator-failed-dispatch');
  try {
    const channel = new BroadcastChannel(dbRestoreOperatorChannelName());
    channel.postMessage({ phase: 'failed' as DbRestorePhase });
    channel.close();
  } catch {
    // ignore
  }
}

export function subscribeDbRestoreOperatorFailed(onFailed: () => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = () => onFailed();

  let channel: BroadcastChannel | null = null;
  const onBroadcast = (ev: MessageEvent) => {
    if (ev.data?.phase === 'failed') {
      handler();
    }
  };

  try {
    channel = new BroadcastChannel(dbRestoreOperatorChannelName());
    channel.addEventListener('message', onBroadcast);
  } catch {
    channel = null;
  }

  return () => {
    channel?.removeEventListener('message', onBroadcast);
    channel?.close();
  };
}

export function dispatchDbRestoreOperatorComplete(): void {
  if (typeof window === 'undefined') return;
  logDbRestoreTrace('12-operator-complete-dispatch');
  try {
    const channel = new BroadcastChannel(dbRestoreOperatorChannelName());
    channel.postMessage({ phase: 'completed' as DbRestorePhase });
    channel.close();
  } catch {
    // ignore
  }
}

export function subscribeDbRestoreOperatorComplete(onComplete: () => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = () => onComplete();

  let channel: BroadcastChannel | null = null;
  const onBroadcast = (ev: MessageEvent) => {
    if (ev.data?.phase === 'completed') {
      handler();
    }
  };

  try {
    channel = new BroadcastChannel(dbRestoreOperatorChannelName());
    channel.addEventListener('message', onBroadcast);
  } catch {
    channel = null;
  }

  return () => {
    channel?.removeEventListener('message', onBroadcast);
    channel?.close();
  };
}

export function redirectToDbRestoreLock(
  phase: Exclude<DbRestorePhase, 'failed'>,
  excludeUserId?: string | null
): void {
  if (typeof window === 'undefined') return;
  dispatchDbRestorePhase(phase, excludeUserId);
  if (isDbRestoreLockRoute()) return;
  window.location.href = buildDbRestoreLockHref(phase, getCurrentPageReturnUrl());
}

export function dispatchDbRestorePhase(
  phase: Exclude<DbRestorePhase, 'failed'>,
  excludeUserId?: string | null
): void {
  if (typeof window === 'undefined') return;

  const payload: DbRestoreSignalState = {
    phase,
    t: Date.now(),
  };
  if (excludeUserId) {
    payload.excludeUserId = excludeUserId;
  }

  try {
    localStorage.setItem(dbRestoreSignalKey(), JSON.stringify(payload));
  } catch {
    // ignore
  }

  try {
    const channel = new BroadcastChannel(dbRestoreChannelName());
    channel.postMessage({ phase });
    channel.close();
  } catch {
    // ignore
  }

  emitLocalDbRestorePhase(phase);
}

export function subscribeDbRestoreLockPage(
  onPhaseChange: (phase: DbRestorePhase) => void
): () => void {
  if (typeof window === 'undefined') return () => {};

  const signalKey = dbRestoreSignalKey();
  const channelName = dbRestoreChannelName();

  const handlePhase = (phase: DbRestorePhase, source: string) => {
    logDbRestoreTrace('L5-lock-user-phase-channel', { phase, source });
    onPhaseChange(phase);
  };

  const onStorage = (e: StorageEvent) => {
    if (e.storageArea !== localStorage || e.key !== signalKey || !e.newValue) return;
    try {
      const parsed = JSON.parse(e.newValue) as { phase?: DbRestorePhase };
      if (parsed.phase === 'restoring' || parsed.phase === 'completed') {
        handlePhase(parsed.phase, 'localStorage-storage-event');
      }
    } catch {
      // ignore
    }
  };

  let channel: BroadcastChannel | null = null;
  const onBroadcast = (ev: MessageEvent) => {
    const phase = ev.data?.phase as DbRestorePhase | undefined;
    if (phase === 'restoring' || phase === 'completed' || phase === 'failed') {
      handlePhase(phase, 'BroadcastChannel');
    }
  };

  const onLocalPhase = (ev: Event) => {
    const phase = (ev as CustomEvent<DbRestorePhase>).detail;
    if (phase === 'restoring' || phase === 'completed' || phase === 'failed') {
      handlePhase(phase, 'local-custom-event');
    }
  };

  window.addEventListener('storage', onStorage);
  window.addEventListener(DB_RESTORE_LOCAL_PHASE_EVENT, onLocalPhase);
  try {
    channel = new BroadcastChannel(channelName);
    channel.addEventListener('message', onBroadcast);
  } catch {
    channel = null;
  }

  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(DB_RESTORE_LOCAL_PHASE_EVENT, onLocalPhase);
    channel?.removeEventListener('message', onBroadcast);
    channel?.close();
  };
}

export function navigateToDashboardAfterDbRestore(returnUrl?: string | null): void {
  if (typeof window === 'undefined') return;
  clearDbRestoreSignalState();
  const target = returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')
    ? returnUrl
    : resolveDashboardRoute();
  window.location.assign(target);
}
